'use client'

import {
  formatCoin,
  formatDate,
  formatPrice,
  shorten,
} from '@polaris/core/utils'
import { useAtomValue } from 'jotai'
import { type ReactNode, useState } from 'react'
import {
  destinationChainAccountAtom,
  destinationChainAddressAtom,
  recoverAccountAtom,
  routeAtom,
  routeMachineSnapshotAtom,
  sourceChainAccountAtom,
  sourceChainAddressAtom,
} from '@/src/stores/route-machine/atoms'
import { WalletIcon } from '../accounts/wallet-icon'
import { RouteDetailsScreen } from '../route-details/screen'
import { Banner } from '../ui/banner'
import { Button } from '../ui/button'
import { ChainImg } from '../ui/chain-img'
import CopyButton from '../ui/copy-button'
import TokenImg from '../ui/token-img'
import { ActionsButton } from './actions-buttons'

export const AutoRecoverSuccess = () => {
  const snapshot = useAtomValue(routeMachineSnapshotAtom)
  const route = useAtomValue(routeAtom)
  const [now] = useState(() => new Date())
  const [showRoute, setShowRoute] = useState(false)

  const refundInfo = snapshot.context.refundInfo

  const sourceAddress = useAtomValue(sourceChainAddressAtom)
  const sourceAccount = useAtomValue(sourceChainAccountAtom)
  const destinationAddress = useAtomValue(destinationChainAddressAtom)
  const destinationAccount = useAtomValue(destinationChainAccountAtom)

  // Recover address should already be in chain format coming from BE
  const recoverAddress = refundInfo?.destinationAddress
  const recoverAccount = useAtomValue(recoverAccountAtom)

  const differentRecoveryDestination =
    recoverAddress &&
    destinationAddress &&
    recoverAddress !== destinationAddress

  // Only render if auto refund info exists and we are in success state
  if (!refundInfo || snapshot.value !== 'FundsRecoverySuccess' || !route)
    return null

  // Full-screen route step list view
  if (showRoute) {
    return <RouteDetailsScreen onBack={() => setShowRoute(false)} />
  }

  const paidUsd = route.usdValueAmountIn
  const recvUsdPlanned = route.usdValueAmountOut

  const recoveredAmount = Number.parseFloat(refundInfo.amount)
  const recoveredUsd = refundInfo.priceUsd
    ? recoveredAmount * refundInfo.priceUsd
    : undefined

  const sourceChain = route.sourceChain
  const destChain = route.destinationChain

  const isSameChain = sourceChain.chain_id === destChain.chain_id

  return (
    <div>
      <div className='flex flex-col px-5 py-4'>
        <div className='text-center mb-3'>
          <p className='text-primary font-semibold'>Funds recovered</p>
          <p className='text-secondary text-sm tracking-tight'>
            {formatDate(now, undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </p>
        </div>
      </div>
      <Banner
        alertTitle='Trade failed'
        description='An issue was encountered with one of the providers in your trade route. Your funds have been recovered safely to your wallet.'
        variant='warning'
        className='pl-6 animate-fade-in'
      />
      <div className='flex flex-col px-5 pt-4 pb-4'>
        <div className='flex flex-col gap-1'>
          <Row title='Paid'>
            <div className='flex items-center gap-1.5 justify-end text-right flex-wrap'>
              <TokenImg
                src={route.InputAssetMetadata.logo_img_url}
                ticker={route.InputAssetMetadata.ticker}
                width={24}
                height={24}
                className='size-6'
                fallbackContainerClassName='size-6'
                unoptimized
              />
              <span className='text-primary text-sm'>
                {formatCoin(
                  {
                    amount: route.AmountIn,
                    denom: route.InputAssetMetadata.ticker,
                  },
                  {
                    maximumFractionDigits: route.InputAssetMetadata.precision,
                    maximumSignificantDigits: 6,
                  },
                )}
              </span>
              <span className='text-secondary text-sm'>
                ({formatPrice(paidUsd)})
              </span>
            </div>
          </Row>

          <Row title='Received'>
            <div className='flex flex-col items-end gap-2 text-right'>
              <div className='flex items-center gap-1.5 flex-wrap'>
                <TokenImg
                  src={route.OutputAssetMetadata.logo_img_url}
                  ticker={route.OutputAssetMetadata.ticker}
                  width={24}
                  height={24}
                  className='size-6 opacity-40'
                  fallbackContainerClassName='size-6'
                  unoptimized
                />
                <span className='text-primary text-sm line-through opacity-60'>
                  {formatCoin(
                    {
                      amount: route.AmountOut,
                      denom: route.OutputAssetMetadata.ticker,
                    },
                    {
                      maximumFractionDigits:
                        route.OutputAssetMetadata.precision,
                      maximumSignificantDigits: 6,
                    },
                  )}
                </span>
                <span className='text-secondary text-sm line-through opacity-60'>
                  ({formatPrice(recvUsdPlanned)})
                </span>
              </div>
              <div className='flex items-center gap-1.5 flex-wrap'>
                <TokenImg
                  src={refundInfo.assetImg}
                  ticker={refundInfo.ticker}
                  width={24}
                  height={24}
                  className='size-6 rounded-full'
                  fallbackContainerClassName='size-6'
                  unoptimized
                />
                <span className='text-primary text-sm'>
                  {formatCoin(
                    { amount: recoveredAmount, denom: refundInfo.ticker },
                    {
                      maximumFractionDigits: refundInfo.precision,
                      maximumSignificantDigits: 6,
                    },
                  )}
                </span>
                {recoveredUsd !== undefined && (
                  <span className='text-secondary text-sm'>
                    ({formatPrice(recoveredUsd)})
                  </span>
                )}
              </div>
            </div>
          </Row>

          {isSameChain ? (
            <Row title='Network'>
              <div className='flex items-end flex-col gap-2 text-right'>
                <div className='flex items-center gap-1.5 flex-wrap'>
                  <ChainImg
                    chainId={destChain.chain_id}
                    chainImg={destChain.chain_img}
                    chainName={destChain.chain_name}
                    containerClassName={
                      destChain.chain_name !== refundInfo.chainName
                        ? 'opacity-40'
                        : undefined
                    }
                    unoptimized
                  />
                  <span
                    className={`text-primary text-sm ${destChain.chain_name !== refundInfo.chainName ? 'line-through opacity-60' : ''}`}
                  >
                    {destChain.chain_name}
                  </span>
                </div>
                {destChain.chain_name !== refundInfo.chainName && (
                  <div className='flex items-center gap-1.5 flex-wrap'>
                    <ChainImg
                      chainId={refundInfo.chainID}
                      chainImg={refundInfo.chainImg}
                      chainName={refundInfo.chainName}
                      unoptimized
                    />
                    <span className='text-primary text-sm'>
                      {refundInfo.chainName}
                    </span>
                  </div>
                )}
              </div>
            </Row>
          ) : (
            <Row title='Networks'>
              <div className='flex items-end flex-col gap-2 text-right'>
                <div className='flex items-center gap-1.5 flex-wrap'>
                  <ChainImg
                    chainId={sourceChain.chain_id}
                    chainImg={sourceChain.chain_img}
                    chainName={sourceChain.chain_name}
                    unoptimized
                  />
                  <span className='text-primary text-sm'>
                    {sourceChain.chain_name}
                  </span>
                  <span className='text-tertiary-100 text-sm'>to</span>
                  <ChainImg
                    chainId={destChain.chain_id}
                    chainImg={destChain.chain_img}
                    chainName={destChain.chain_name}
                    containerClassName={
                      destChain.chain_name !== refundInfo.chainName
                        ? 'opacity-40'
                        : undefined
                    }
                    unoptimized
                  />
                  <span
                    className={`text-primary text-sm ${destChain.chain_name !== refundInfo.chainName ? 'line-through opacity-60' : ''}`}
                  >
                    {destChain.chain_name}
                  </span>
                </div>
                {destChain.chain_name !== refundInfo.chainName && (
                  <div className='flex items-center gap-1.5 flex-wrap'>
                    <ChainImg
                      chainId={refundInfo.chainID}
                      chainImg={refundInfo.chainImg}
                      chainName={refundInfo.chainName}
                      unoptimized
                    />
                    <span className='text-primary text-sm'>
                      {refundInfo.chainName}
                    </span>
                  </div>
                )}
              </div>
            </Row>
          )}

          <Row title='From'>
            <div className='flex items-center gap-1.5 justify-end text-right flex-wrap'>
              {sourceAccount?.provider_info?.img ? (
                <WalletIcon
                  logoUrl={sourceAccount.provider_info.img}
                  walletName={sourceAccount.provider_info.name}
                  size={24}
                  className='size-6'
                />
              ) : null}
              <span className='text-primary text-sm'>
                {shorten(
                  sourceAddress ?? route?.sourceChainEcosystemAddress ?? '',
                )}
              </span>
            </div>
          </Row>

          <Row title='To'>
            <div className='flex items-end flex-col gap-2 text-right'>
              {(() => {
                const isDifferent = Boolean(differentRecoveryDestination)
                if (!destinationAddress || !isDifferent) return null
                return (
                  <div className='flex items-center gap-1.5 flex-wrap'>
                    {destinationAccount?.provider_info?.img ? (
                      <WalletIcon
                        logoUrl={destinationAccount.provider_info.img}
                        walletName={destinationAccount.provider_info.name}
                        size={24}
                        className='size-6 opacity-40'
                      />
                    ) : null}
                    <span className='text-primary text-sm line-through opacity-60'>
                      {shorten(
                        destinationAddress ??
                          route?.destinationChainEcosystemAddress ??
                          '',
                      )}
                    </span>
                  </div>
                )
              })()}
              <div className='flex items-center gap-1.5 flex-wrap'>
                {recoverAccount?.provider_info?.img ? (
                  <WalletIcon
                    logoUrl={recoverAccount.provider_info.img}
                    walletName={recoverAccount.provider_info.name}
                    size={24}
                    className='size-6'
                  />
                ) : null}
                <span className='text-primary text-sm'>
                  {shorten(refundInfo.destinationAddress)}
                </span>
              </div>
            </div>
          </Row>

          <Row title='Route'>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setShowRoute((v) => !v)}
            >
              Show route
            </Button>
          </Row>

          <Row title='Trade ID' data-testid='trx-id' data-trxid={route.ID}>
            <CopyButton
              contentToCopy={route.ID}
              displayContent={shorten(route.ID)}
            />
          </Row>
        </div>
      </div>
      <ActionsButton />
    </div>
  )
}

const Row = ({
  title,
  children,
  ...rest
}: {
  title: ReactNode
  children: ReactNode
} & React.ComponentProps<'div'>) => (
  <div
    className='flex items-start justify-between w-full min-h-8 py-1 text-primary text-sm'
    {...rest}
  >
    <span className='text-secondary'>{title}</span>
    {children}
  </div>
)
